import * as THREE from "three";
import { LevelSim, SimEventKind } from "@highmarch/domain";
import { roadY } from "./common";
import { GroundMesh } from "../groundMesh";
import { LAUNCH, ballisticStep } from "../unitFx";
import type { VFX } from "../../../../../art/vfx.js";
import { BOSS } from "./bossFx";
import { BossStage } from "./bossStage";
import { BLAST_RADIUS, BLAST_TOWERS, DIE, Death, EnemyView } from "./enemyView";
import { wyrmFall, wyrmFallStep } from "./wyrmView";

/** Enemy deaths (enemies.ts EnemyViews.kill starts them): the blast that throws a body, and each frame of the fall and dissolve. */

const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();

/** Where the blast towers' shots of this tick landed. */
export function collectShots(sim: LevelSim, impacts: number[]): void {
  impacts.length = 0;
  for (const ev of sim.events.all()) {
    if (ev.kind !== SimEventKind.TowerFired) continue;
    const t = sim.findTower(ev.a);
    if (!t || !BLAST_TOWERS.has(sim.balance.towers[t.archetype].id)) continue;
    const target = sim.enemySlots.find((x) => x.id === ev.b);
    if (target) impacts.push(target.x.toNumber(), target.y.toNumber(), t.cell.x, t.cell.y);
  }
}

/** Unit direction away from this tick's nearest blast impact within reach, or null when none was near. */
export function blastFrom(v: EnemyView, impacts: readonly number[]): { x: number; z: number } | null {
  let best = -1, bestD2 = BLAST_RADIUS * BLAST_RADIUS;
  for (let i = 0; i < impacts.length; i += 4) {
    const dx = v.cur.x - impacts[i], dz = v.cur.z - impacts[i + 1];
    const d2 = dx * dx + dz * dz;
    if (d2 <= bestD2) { bestD2 = d2; best = i; }
  }
  if (best < 0) return null;
  let dx = v.cur.x - impacts[best], dz = v.cur.z - impacts[best + 1];
  if (dx * dx + dz * dz < 0.0025) { dx = v.cur.x - impacts[best + 2]; dz = v.cur.z - impacts[best + 3]; }   // the shot's own target: away from the tower
  const d = Math.hypot(dx, dz) || 1;
  return { x: dx / d, z: dz / d };
}

/** Per-archetype death beat through the sprite VFX (units F4.3): the healer releases green motes. */
export function deathBeat(v: EnemyView, fx: Pick<VFX, "sparkles"> | null): void {
  if (!fx || v.archId !== "healer") return;
  const p = v.root.position;
  fx.sparkles(new THREE.Vector3(p.x, p.y + v.height * v.scale * 0.6, p.z), 5, { color: [0.55, 1, 0.45], radius: 0.2, rise: 0.8, life: 0.8, size: 0.1 });
}

/** Advances a dying view; true once it has fully dissolved. */
export function dieStep(v: EnemyView, dt: number, reduced: boolean, time: number, stage: BossStage, ring: (v: EnemyView, hover: number, fade: number) => void): boolean {
  v.dead += dt;
  if (v.death === Death.Launch) {
    ballisticStep(v.fly, v.height * v.scale * 0.3, dt);
    if (!v.fly.rest) { _q.setFromAxisAngle(v.spinAxis, v.spinRate * dt * (v.fly.bounced ? 0.4 : 1)); v.body.quaternion.premultiply(_q); }
    // spin about the body's middle, not its feet: place the feet so the centre sits on the arc
    _v.set(0, v.height * v.scale * 0.5, 0).applyQuaternion(v.body.quaternion);
    // the arc is over the ground it flies across: where it lands may be up or down the slope from where it died
    const p = v.root.position, below = GroundMesh.drawnAt(p.x + v.fly.x, p.z + v.fly.z) - p.y;
    v.body.position.set(v.fly.x - _v.x, roadY() + below + v.fly.y - _v.y, v.fly.z - _v.z);
  } else {
    v.rig.update(dt);
    if (v.torso && v.torso.position.y < DIE.sinkFloor) v.torso.position.y = DIE.sinkFloor;   // the baked sink, neutralised
    if (v.death === Death.Spiral) {
      const u = Math.min(1, v.dead / DIE.spiralFall);
      v.body.rotation.y += DIE.spiralYaw * dt;
      v.body.position.y = roadY() + v.fallFrom * (1 - u * u);
    } else if (v.death === Death.Fall) wyrmFallStep(v);           // M40: the Wyrm's own fall (its clip spins it)
  }
  const d = v.dead - v.dissolveAt;
  if (v.look && d > 0) v.look.dissolve = d / v.dissolveFor;
  if (v.bs && v.death !== Death.Flee) stage.dieFrame(v.bs, v.dead, v.bodyH * v.scale, reduced);   // a Wyrm that got away pays no coins
  v.look?.update(dt, time, reduced);
  v.bar.update(dt);
  const fade = 1 - Math.max(0, d) / DIE.ringFade;
  const hover = v.death === Death.Launch ? Math.max(0, v.fly.y - v.height * v.scale * 0.5) : Math.max(0, v.body.position.y - roadY());
  if (fade > 0) ring(v, hover, fade);
  return d >= v.dissolveFor || !v.look;
}

/**
 * How a killed HQ unit goes (units F4): a kill beside a cannon/mortar/fire impact (`blast`, the direction away from it)
 * flies on a ballistic arc, a flyer spirals down, everything else plays the baked topple; each then dissolves. The `die`
 * clip plays solo: it cuts the `hit` the killing blow has just started (in-game check 2026-10-22: averaged with it, a
 * death began at half its reach for the hit's 0.37 s).
 */
export function startDeath(v: EnemyView, reduced: boolean, blast: { x: number; z: number } | null, fx: Pick<VFX, "sparkles"> | null): void {
  v.body.scale.setScalar(v.scale);
  v.ik?.reset();                                       // the clip's own legs for the fall
  v.body.rotation.x = v.foot.pitch;                    // the lean goes; the tilt with the slope stays, or a boss's front sinks
  v.dissolveFor = v.boss ? BOSS.dissolveSeconds : DIE.dissolve;
  if (v.bomb) v.bomb.scale.setScalar(0);              // the bomber's bomb is lobbed at a tower as it dies (BombDropped)
  if (v.wyrm) { wyrmFall(v); return; }               // M40: the Starfall Wyrm falls its own way (wyrmView.ts)
  if (blast && !v.boss && !v.air && !reduced) {
    v.death = Death.Launch;
    v.rig.reset();                                   // no clip in flight: the body tumbles in its rest pose
    const halfH = v.height * v.scale * 0.5;
    v.fly = { x: 0, y: halfH, z: 0, vx: blast.x * LAUNCH.away, vy: LAUNCH.up, vz: blast.z * LAUNCH.away, bounced: false, rest: false };
    const a = Math.random() * Math.PI * 2;
    v.spinAxis.set(Math.cos(a), 0, Math.sin(a));
    v.spinRate = LAUNCH.spin * (0.8 + Math.random() * 0.4);
    v.dissolveAt = DIE.launchStart;
  } else if (v.air) {
    v.death = Death.Spiral;
    v.rig.playOnce("die", true, null, { solo: true });
    v.fallFrom = v.body.position.y - roadY();
    v.dissolveAt = DIE.spiralFall;
  } else {
    v.death = Death.Topple;
    const len = v.rig.playOnce("die", true, null, { solo: true });
    v.dissolveAt = v.boss ? BOSS.crackSeconds : len > 0 ? Math.min(DIE.toppleStart, len * 0.66) : 0;
    if (v.archId === "healer") deathBeat(v, fx);
  }
}
